// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 34-workloads.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import type { z } from 'zod';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import type { GetAppResultSchema } from '@greenlight/shared';

type Workload = z.infer<typeof GetAppResultSchema>['workloads'][number];

const DAY_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * Human-readable form of a 5-field cron expression for the common cases a demo
 * cron uses (daily/weekly at a time, hourly, every-N-minutes). Falls back to the
 * raw expression for anything it doesn't recognize, so it never lies.
 */
export function describeSchedule(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [min, hour, dom, mon, dow] = parts as [string, string, string, string, string];
  const pad = (s: string): string => s.padStart(2, '0');
  const everyDayOfMonth = dom === '*' && mon === '*';

  if (hour === '*' && everyDayOfMonth && dow === '*') {
    const everyN = /^\*\/(\d+)$/.exec(min);
    if (everyN) return `Every ${everyN[1]} minutes`;
    if (min === '0') return 'Hourly';
  }

  const atTime =
    /^\d{1,2}$/.test(min) && /^\d{1,2}$/.test(hour) ? `${pad(hour)}:${pad(min)} UTC` : null;
  if (atTime && everyDayOfMonth) {
    if (dow === '*') return `Daily ${atTime}`;
    if (/^[0-6]$/.test(dow)) return `Weekly on ${DAY_OF_WEEK[Number(dow)]} ${atTime}`;
  }
  return expr;
}

/** Absolute UTC instant for a cron run, e.g. "Jan 14, 06:00 UTC". */
function formatUtc(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date}, ${time} UTC`;
}

const WORKLOAD_STATUS: Record<string, StatusBadgeVariant> = {
  active: 'active',
  failed: 'failed',
  suspended: 'inactive',
  pending: 'pending',
};

function statusVariant(status: string): StatusBadgeVariant {
  return WORKLOAD_STATUS[status] ?? 'pending';
}

/** Neutral chip naming the workload kind (web / cron). */
function KindChip({ kind }: { kind: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-gl bg-muted/60 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide text-foreground">
      {kind}
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

export function WorkloadRow({ workload }: { workload: Workload }): React.JSX.Element {
  const spec = workload.spec as { image?: unknown; port?: unknown; routes?: unknown };
  const image = spec.image != null ? String(spec.image) : null;
  const port = spec.port != null ? String(spec.port) : null;
  const routes = Array.isArray(spec.routes) ? spec.routes.map(String) : [];
  const isCron = workload.kind === 'cron';

  return (
    <div className="space-y-2 py-3 first:pt-0 last:pb-0" data-testid={`workload-${workload.name}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">{workload.name}</span>
        <KindChip kind={workload.kind} />
        <StatusBadge variant={statusVariant(workload.status)}>{workload.status}</StatusBadge>
      </div>
      <div className="space-y-1.5">
        {image && (
          <Field label="Image">
            <MonoValue>{image}</MonoValue>
          </Field>
        )}
        {isCron ? (
          <>
            {workload.cron_schedule && (
              <Field label="Schedule">
                {describeSchedule(workload.cron_schedule)}{' '}
                <MonoValue>{workload.cron_schedule}</MonoValue>
              </Field>
            )}
            {workload.last_run_at && (
              <Field label="Last run">{relativeTime(workload.last_run_at)}</Field>
            )}
            {workload.next_run_at && (
              <Field label="Next run">{formatUtc(workload.next_run_at)}</Field>
            )}
          </>
        ) : (
          <>
            {port && <Field label="Port">{port}</Field>}
            {routes.length > 0 && (
              <Field label="Routes">
                {routes.map((r) => (
                  <MonoValue key={r} className="mr-1">
                    {r}
                  </MonoValue>
                ))}
              </Field>
            )}
          </>
        )}
      </div>
    </div>
  );
}
